import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { designServices, marketingGroups, marketingMenuFeature, marketingServices } from '../../data/navigation';
import type { NavSubItem } from '../../data/navigation';
import { gallerySites } from '../../data/gallery';
import { NavIconGlyph } from './NavIconGlyph';
type PanelProps = {
  onNavigate: () => void;
};
const featuredSites = gallerySites.filter(site => site.featured).slice(0, 3);
function MenuLink({
  item,
  onNavigate,
  large = false
}: {
  item: NavSubItem;
  onNavigate: () => void;
  large?: boolean;
}) {
  return <a href={item.href} onClick={onNavigate} className="group flex items-start gap-3 rounded-lg p-2.5 transition-colors duration-150 ease-out hover:bg-brand-tint focus:outline-none focus-visible:bg-brand-tint" data-id="element-627" data-mp="2jx2gdr6yfop">
      <span className={`flex flex-shrink-0 items-center justify-center rounded-md bg-brand-light text-brand-black transition-colors duration-150 ease-out group-hover:bg-brand-red group-hover:text-white ${large ? 'h-11 w-11' : 'h-9 w-9'}`} data-id="element-628" data-mp="299brzs68guyc">
        <NavIconGlyph icon={item.icon} className={large ? 'h-5 w-5' : 'h-[18px] w-[18px]'} data-id="element-629" data-mp="2lufgdncznrvx" />
      </span>
      <span className="min-w-0" data-id="element-630" data-mp="2gusz92u1hrhu">
        <span className={`block font-semibold text-brand-black ${large ? 'text-base' : 'text-sm'}`} data-id="element-631" data-mp="2gvvrpf70g1zv">{item.name}</span>
        <span className={`mt-0.5 block text-brand-gray ${large ? 'text-sm' : 'text-xs'}`} data-id="element-632" data-mp="2cvbwih5zz8gd">{item.description}</span>
      </span>
    </a>;
}
export function MarketingMegaPanel({
  onNavigate
}: PanelProps) {
  const byName = new Map(marketingServices.map(item => [item.name, item]));
  const feature = marketingMenuFeature;
  return <div className="grid grid-cols-12 gap-8" data-id="element-633" data-mp="2vjyyurwshjq9">
      <div className="col-span-8 grid grid-cols-3 gap-6" data-id="element-634" data-mp="2vj73ni73to0i">
        {marketingGroups.map(group => <div key={group.title} data-id="element-635" data-mp="1p4ocwympyudz">
            <p className="px-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-636" data-mp="1p1jzdand6man">{group.title}</p>
            <ul className="mt-3 space-y-1" data-id="element-637" data-mp="1llz5cm9qenhe">
              {group.items.map(name => {
            const item = byName.get(name);
            return item ? <li key={name} data-id="element-638" data-mp="1qkafky3xv005">
                    <MenuLink item={item} onNavigate={onNavigate} data-id="element-639" data-mp="1clkj9koy14cu" />
                  </li> : null;
          })}
            </ul>
          </div>)}
      </div>

      <a href={feature.href} onClick={onNavigate} className="group relative col-span-4 flex min-h-[260px] overflow-hidden rounded-xl bg-brand-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-640" data-mp="1goql8g50w9di">
        <img src={feature.image} alt="" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-641" data-mp="sz4n7w0e0073" />
        <span className="relative mt-auto block p-6" data-id="element-642" data-mp="ow81vxjgppzg">
          <span className="block text-xs font-bold uppercase tracking-[0.14em] text-white" data-id="element-643" data-mp="ouwwxuea6c6r">{feature.kicker}</span>
          <span className="mt-2 block text-xl font-extrabold leading-snug text-white" data-id="element-644" data-mp="5brd79fya4sk">{feature.title}</span>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-red px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out group-hover:bg-brand-red-dark" data-id="element-645" data-mp="eamjsmmwpgwe">
            {feature.cta}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-646" data-mp="o80ojozgwh8v" />
          </span>
        </span>
      </a>
    </div>;
}
export function DesignMegaPanel({
  onNavigate
}: PanelProps) {
  return <div className="grid grid-cols-12 gap-8" data-id="element-647" data-mp="xz3rkwbyj6as">
      <div className="col-span-4" data-id="element-648" data-mp="xxsjikbhbcdv">
        <p className="px-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-649" data-mp="szr4qbl35z87">Web Design</p>
        <ul className="mt-3 space-y-1" data-id="element-650" data-mp="16yqrtesya629">
          {designServices.map(item => <li key={item.name} data-id="element-651" data-mp="12076ngm6kyay">
              <MenuLink item={item} onNavigate={onNavigate} large data-id="element-652" data-mp="1218gjhrgner3" />
            </li>)}
        </ul>
        <p className="mt-5 border-t border-gray-100 px-2.5 pt-5 text-sm leading-relaxed text-gray-600" data-id="element-653" data-mp="2wfmbl7xm0hsx">
          Built in 3 days. Changed in minutes by phone, text, email, Slack or online.
        </p>
      </div>

      <div className="col-span-8" data-id="element-654" data-mp="366qp037mzuln">
        <div className="flex items-baseline justify-between px-0.5" data-id="element-655" data-mp="3a7sqyrsxukrb">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-656" data-mp="3a70ulerioizz">Recent work</p>
          <a href="#/design/gallery" onClick={onNavigate} className="group inline-flex items-center gap-1 text-sm font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:underline" data-id="element-657" data-mp="2dje0voltveq8">
            See all {gallerySites.length} sites
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-658" data-mp="1s8yvw4078du" />
          </a>
        </div>
        <ul className="mt-3 grid grid-cols-3 gap-4" data-id="element-659" data-mp="6t6i8c0r28gx">
          {featuredSites.map(site => <li key={site.id} data-id="element-660" data-mp="gx4qxdq13qy1">
              <a href="#/design/gallery" onClick={onNavigate} className="group block focus:outline-none" data-id="element-661" data-mp="bw7dtfjvln3e">
                <span className="block aspect-[4/3] overflow-hidden rounded-lg border border-gray-200 bg-gray-100 group-focus-visible:ring-2 group-focus-visible:ring-brand-red" data-id="element-662" data-mp="734ip2zga5l3">
                  <img src={site.image} alt="" loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-663" data-mp="3ubhtru2v8g7z" />
                </span>
                <span className="mt-2 block text-sm font-semibold text-brand-black transition-colors duration-150 ease-out group-hover:text-brand-red" data-id="element-664" data-mp="3gbyora5cpkzw">
                  {site.name}
                </span>
                <span className="block text-xs text-brand-gray" data-id="element-665" data-mp="1ejk78c6pyik0">{site.industry}</span>
              </a>
            </li>)}
        </ul>
      </div>
    </div>;
}